/* ==========================================================================
   PER-PANEL FRESHNESS — is this panel showing now, and how do we know?

   THE BAR §6: "the operator must never have to ask 'is this now, and is this
   real?' Both answers are on screen at all times, PER PANEL." §13 tell 24
   sharpens it: a panel that says "Latest" without saying how late is lying,
   and tell 27 forbids one global light standing in for several channels.

   The failure this module exists to prevent is the quiet one. React Query
   keeps the last good `data` when a refetch fails, so a board wired to
   `data` alone renders exactly the same pixels whether the API answered
   200ms ago or died four minutes back — and every client-side age counter on
   it keeps ticking up, which makes a frozen row look *more* current the
   longer the outage runs. A hard-coded `live` prop is that bug in one word.

   So liveness is derived, per source, from three facts React Query already
   holds:

     dataUpdatedAt   when this query last SUCCEEDED (unchanged by a failure)
     isError         the last attempt failed and its retry failed too
     fetchStatus     'paused' = offline / focus-gated, nothing is being tried

   A source is trusted while its last success is inside twice its own poll
   cadence AND its last attempt succeeded. Twice, not once: one skipped beat
   at a 3s cadence is scheduler jitter, two is an outage. Past that the panel
   must say so, dim what it is still drawing, and — the part that matters —
   STOP counting, because an age computed against `Date.now()` on a dead read
   is a fabricated number, and §11.10 is clear that a fabricated freshness
   figure is the worst defect available to a console like this one.
   ========================================================================== */

/** The shape this module needs off a React Query result. */
export interface QueryLike {
  dataUpdatedAt: number;
  isError: boolean;
  error: unknown;
  fetchStatus: 'fetching' | 'paused' | 'idle';
}

export interface Source {
  /** The endpoint, verbatim, so the operational sentence can name it. */
  endpoint: string;
  /** The cadence this panel is promised, ms. */
  intervalMs: number;
  /** Epoch ms of the last SUCCESSFUL read; 0 when there has never been one. */
  lastGoodAt: number;
  /** The last attempt failed (after its retry). */
  failing: boolean;
  /** Nothing is being attempted — offline, or the shell's live hold. */
  paused: boolean;
  /** The failure, verbatim. Provenance, not the headline. */
  reason: string | null;
}

/** Bind one query to the endpoint it reads and the cadence it promises. */
export function source(
  endpoint: string,
  intervalMs: number,
  q: QueryLike,
): Source {
  return {
    endpoint,
    intervalMs,
    lastGoodAt: q.dataUpdatedAt,
    failing: q.isError,
    paused: q.fetchStatus === 'paused',
    reason: q.isError ? shortReason(q.error) : null,
  };
}

/**
 * `TypeError: Failed to fetch` is what the browser says; it is not what the
 * operator needs on the first line. Kept, trimmed, and demoted to a dim
 * suffix by the caller.
 */
function shortReason(err: unknown): string {
  const s = err instanceof Error ? err.message : String(err ?? '');
  const first = s.split('\n')[0] ?? '';
  return first.length > 120 ? `${first.slice(0, 119)}…` : first;
}

export interface Health {
  /** True only while every source behind this panel is inside its budget. */
  live: boolean;
  /** Epoch ms of the oldest last-good read behind this panel. */
  lastGoodAt: number | null;
  /** Age of that read against the ticking clock. Null before the first one. */
  ageMs: number | null;
  /**
   * THE CLOCK EVERY AGE INSIDE THIS PANEL IS MEASURED AGAINST.
   *
   * `now` while the panel is live; the last good read once it is not — which
   * freezes every "in state 7m 14s" at the value it genuinely had when the
   * data was genuinely read. A dead row does not get to keep ticking.
   */
  asOf: number;
  /** The source that is holding the panel back, and the one to name. */
  worst: Source;
}

export function health(sources: readonly Source[], now: number): Health {
  const first = sources[0];
  if (first === undefined) {
    /* Not reachable in practice — the type keeps the callers honest. */
    const stub: Source = {
      endpoint: '', intervalMs: 0, lastGoodAt: 0, failing: false, paused: false, reason: null,
    };
    return { live: true, lastGoodAt: null, ageMs: null, asOf: now, worst: stub };
  }

  const good = sources.filter((s) => s.lastGoodAt > 0).map((s) => s.lastGoodAt);
  const lastGoodAt = good.length === 0 ? null : Math.min(...good);
  const ageMs = lastGoodAt === null ? null : Math.max(0, now - lastGoodAt);

  const ok = (s: Source): boolean => {
    if (s.failing) return false;
    if (s.lastGoodAt === 0) return !s.paused;
    return now - s.lastGoodAt <= 2 * s.intervalMs;
  };

  const live = sources.every(ok);
  /* The one worth naming: whichever failed, else whichever is furthest past
     its cadence. Two dead endpoints get one sentence, about the older. */
  const worst =
    sources.find((s) => s.failing) ??
    [...sources].sort((a, b) => (a.lastGoodAt || Infinity) - (b.lastGoodAt || Infinity))[0] ??
    first;

  return {
    live,
    lastGoodAt,
    ageMs,
    asOf: live || lastGoodAt === null ? now : lastGoodAt,
    worst,
  };
}
